/**
 * Free Gift Auto-Add Functionality
 * Pre-adds a free gift to cart when user loads the website
 * Shows a toast notification and enforces max quantity of 1
 */

(function() {
  'use strict';

  // Get settings from theme configuration
  function getSettings() {
    const settings = window.MinimogSettings || {};
    return {
      enabled: settings.enable_free_gift_auto_add === true,
      variantId: settings.free_gift_variant_id || '62572293062815',
      message: settings.free_gift_notification_message || 'Free Gift Added To Cart'
    };
  }

  let giftSettings = null;
  let isAddingGift = false;

  /**
   * Check if gift variant is already in cart and get its info
   */
  async function getGiftCartInfo() {
    if (!giftSettings) return { exists: false, quantity: 0, key: null };
    
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return { exists: false, quantity: 0, key: null };
      
      const cart = await response.json();
      const giftItem = cart.items.find(item => item.variant_id.toString() === giftSettings.variantId);
      
      if (giftItem) {
        return {
          exists: true,
          quantity: giftItem.quantity,
          key: giftItem.key
        };
      }
      
      return { exists: false, quantity: 0, key: null };
    } catch (error) {
      console.error('Free Gift Auto-Add: Error checking cart:', error);
      return { exists: false, quantity: 0, key: null };
    }
  }

  /**
   * Ensure gift quantity is max 1 (can be removed but not increased above 1)
   */
  async function enforceGiftQuantity() {
    if (!giftSettings || !giftSettings.enabled) return;
    
    try {
      const giftInfo = await getGiftCartInfo();
      if (giftInfo.exists && giftInfo.quantity > 1 && giftInfo.key) {
        // Update quantity to 1
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: giftInfo.key,
            quantity: 1
          })
        });
        
        if (response.ok) {
          // Update cart count
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            updateCartCount(cart.item_count);
          }
          
          // Emit events to refresh cart drawer
          emitCartUpdateEvents();
        }
      }
    } catch (error) {
      console.error('Free Gift Auto-Add: Error enforcing gift quantity:', error);
    }
  }

  /**
   * Update cart count in header
   */
  function updateCartCount(count) {
    const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .sf-cart-count');
    cartCountElements.forEach(el => {
      el.textContent = count;
    });
  }

  /**
   * Emit events to trigger cart drawer refresh
   */
  function emitCartUpdateEvents() {
    if (window.MinimogEvents && typeof window.MinimogEvents.emit === 'function') {
      window.MinimogEvents.emit('CART_UPDATED');
      window.MinimogEvents.emit('ON_CART_UPDATED');
    }
    
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  /**
   * Add gift variant to cart
   */
  async function addGiftToCart() {
    if (isAddingGift || !giftSettings) {
      return false;
    }
    
    try {
      isAddingGift = true;
      
      // Check if gift already exists
      const giftInfo = await getGiftCartInfo();
      if (giftInfo.exists) {
        // If quantity is more than 1, reduce it
        if (giftInfo.quantity > 1) {
          await enforceGiftQuantity();
        }
        return true;
      }
      
      // Add gift to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: giftSettings.variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Free Gift Auto-Add: Error adding gift:', error);
        return false;
      }

      const data = await response.json();
      console.log('Free Gift Auto-Add: Gift added successfully');
      
      // Update cart count
      try {
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          updateCartCount(cart.item_count);
        }
      } catch (e) {
        // Silent fail
      }
      
      return true;
    } catch (error) {
      console.error('Free Gift Auto-Add: Error adding gift to cart:', error);
      return false;
    } finally {
      isAddingGift = false;
    }
  }

  /**
   * Show toast notification for free gift
   */
  function showGiftNotification() {
    if (!giftSettings) return;
    
    // Always use fallback toast to ensure it shows
    createFallbackToast();
    
    // Also try theme notification if available
    if (window.MinimogTheme && window.MinimogTheme.Notification && typeof window.MinimogTheme.Notification.show === 'function') {
      try {
        window.MinimogTheme.Notification.show({
          target: document.body,
          method: 'appendChild',
          type: 'success',
          message: giftSettings.message,
          last: 3000,
          sticky: false
        });
      } catch (e) {
        // Fallback already shown above
      }
    }
  }

  /**
   * Handle product added to cart - show notification
   */
  function handleProductAdded(eventOrItem) {
    if (!giftSettings || !giftSettings.enabled) return;

    // Extract item data from event
    let item = null;
    if (eventOrItem) {
      if (eventOrItem.detail) {
        item = eventOrItem.detail;
      } else if (eventOrItem.variant_id || eventOrItem.id) {
        item = eventOrItem;
      }
    }

    // Skip if the added item is the gift itself
    if (item && giftSettings) {
      const variantId = item.variant_id || item.id;
      if (variantId && variantId.toString() === giftSettings.variantId) {
        return; // Don't show notification when gift is added
      }
    }

    // Show notification when any other product is added
    showGiftNotification();
  }

  /**
   * Fallback toast notification if theme notification system is not available
   */
  function createFallbackToast() {
    // Remove existing toast if any
    const existingToast = document.querySelector('.free-gift-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast element
    const toast = document.createElement('div');
    toast.className = 'free-gift-toast';
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #8B4513 0%, #A0522D 100%);
      color: #FFF8E7;
      padding: 16px 20px;
      border-radius: 16px;
      box-shadow: 0 8px 24px rgba(139, 69, 19, 0.3), 0 0 0 2px rgba(255, 248, 231, 0.2);
      z-index: 9999;
      font-size: 14px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 12px;
      animation: slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
      max-width: 90%;
      width: auto;
      min-width: 280px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    `;

    // Add cute gift icon and message
    toast.innerHTML = `
      <div style="
        background: rgba(255, 248, 231, 0.2);
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        animation: bounce 0.6s ease-in-out;
      ">
        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
        </svg>
      </div>
      <span style="flex: 1; line-height: 1.4;">${giftSettings.message}</span>
      <div style="
        width: 6px;
        height: 6px;
        background: #FFD700;
        border-radius: 50%;
        box-shadow: 0 0 8px rgba(255, 215, 0, 0.6);
        animation: pulse 2s ease-in-out infinite;
      "></div>
    `;

    // Add animation styles if not already added
    if (!document.querySelector('#free-gift-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'free-gift-toast-styles';
      style.textContent = `
        @keyframes slideInRight {
          from {
            transform: translateX(120%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(120%);
            opacity: 0;
          }
        }
        @keyframes bounce {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.2);
          }
        }
        .free-gift-toast {
          animation: slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        .free-gift-toast.removing {
          animation: slideOutRight 0.3s ease-out;
        }
        
        /* Mobile Responsive */
        @media (max-width: 768px) {
          .free-gift-toast {
            top: 16px !important;
            right: 16px !important;
            left: 16px !important;
            max-width: calc(100% - 32px) !important;
            width: auto !important;
            min-width: auto !important;
            padding: 14px 18px !important;
            font-size: 13px !important;
            border-radius: 14px !important;
          }
          
          .free-gift-toast > div:first-child {
            width: 36px !important;
            height: 36px !important;
          }
          
          .free-gift-toast svg {
            width: 20px !important;
            height: 20px !important;
          }
        }
        
        @media (max-width: 480px) {
          .free-gift-toast {
            top: 12px !important;
            right: 12px !important;
            left: 12px !important;
            max-width: calc(100% - 24px) !important;
            padding: 12px 16px !important;
            font-size: 12px !important;
            gap: 10px !important;
          }
          
          .free-gift-toast > div:first-child {
            width: 32px !important;
            height: 32px !important;
          }
          
          .free-gift-toast svg {
            width: 18px !important;
            height: 18px !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);

    // Remove toast after 3 seconds
    setTimeout(() => {
      toast.classList.add('removing');
      setTimeout(() => {
        if (toast.parentNode) {
          toast.remove();
        }
      }, 300);
    }, 3000);
  }

  /**
   * Setup listeners for product additions
   */
  function setupProductAddListeners() {
    // Wait a bit for MinimogEvents to be available
    setTimeout(() => {
      // Listen via MinimogEvents
      if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function') {
        window.MinimogEvents.subscribe('ON_ITEM_ADDED', handleProductAdded);
      } else if (window.MinimogEvents && typeof window.MinimogEvents.on === 'function') {
        window.MinimogEvents.on('ON_ITEM_ADDED', handleProductAdded);
      } else if (window.MinimogEvents && typeof window.MinimogEvents.addEventListener === 'function') {
        window.MinimogEvents.addEventListener('ON_ITEM_ADDED', handleProductAdded);
      }
    }, 500);

    // Intercept Shopify.onItemAdded
    if (window.Shopify) {
      const originalOnItemAdded = window.Shopify.onItemAdded;
      if (originalOnItemAdded) {
        window.Shopify.onItemAdded = function(item, openDrawer) {
          // Call original function
          if (typeof originalOnItemAdded === 'function') {
            originalOnItemAdded.call(this, item, openDrawer);
          }
          
          // Show notification
          handleProductAdded(item);
        };
      } else {
        // If onItemAdded doesn't exist, create it
        window.Shopify.onItemAdded = function(item, openDrawer) {
          handleProductAdded(item);
        };
      }
    }

    // Intercept fetch calls to /cart/add.js
    if (!window.__freeGiftFetchIntercepted) {
      const originalFetch = window.fetch;
      window.fetch = async function(...args) {
        const url = args[0];
        
        // Check if this is a cart add request
        if (typeof url === 'string' && url.includes('/cart/add.js') && args[1] && args[1].method === 'POST') {
          // Check if it's adding the gift itself
          if (args[1].body) {
            try {
              const body = typeof args[1].body === 'string' ? JSON.parse(args[1].body) : args[1].body;
              const variantId = body.id || (body.items && body.items[0] && body.items[0].id);
              
              // If it's not the gift, show notification after it's added
              if (variantId && giftSettings && variantId.toString() !== giftSettings.variantId) {
                // Wait for the fetch to complete, then show notification
                const response = await originalFetch.apply(this, args);
                
                if (response.ok) {
                  try {
                    const data = await response.clone().json();
                    setTimeout(() => {
                      handleProductAdded(data);
                    }, 100);
                  } catch (e) {
                    // If not JSON, show notification anyway
                    setTimeout(() => {
                      showGiftNotification();
                    }, 100);
                  }
                }
                
                return response;
              }
            } catch (e) {
              // If parsing fails, proceed normally
            }
          }
        }
        
        return originalFetch.apply(this, args);
      };
      window.__freeGiftFetchIntercepted = true;
    }

    // Also listen for custom DOM events
    document.addEventListener('ON_ITEM_ADDED', handleProductAdded);
    document.addEventListener('cart:item-added', handleProductAdded);
  }

  /**
   * Monitor cart quantity changes and prevent increase above 1
   */
  function setupQuantityMonitoring() {
    // Monitor quantity input changes in cart drawer
    document.addEventListener('change', async function(e) {
      if (!giftSettings || !giftSettings.enabled) return;
      
      const target = e.target;
      
      // Check if this is a quantity input for the gift
      if (target.type === 'number' && target.classList.contains('scd-item__qty_input')) {
        const itemKey = target.getAttribute('data-id');
        if (!itemKey) return;
        
        // Get cart to check if this is the gift
        try {
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            const giftItem = cart.items.find(item => item.variant_id.toString() === giftSettings.variantId);
            
            if (giftItem && giftItem.key === itemKey) {
              const newQuantity = parseInt(target.value) || 0;
              
              // If quantity is more than 1, reset to 1
              if (newQuantity > 1) {
                target.value = 1;
                await enforceGiftQuantity();
                
                // Show a subtle message (optional)
                const message = document.createElement('div');
                message.textContent = 'Free gift quantity cannot exceed 1';
                message.style.cssText = 'color: #ef4444; font-size: 12px; margin-top: 4px;';
                message.className = 'gift-quantity-warning';
                
                // Remove existing warning
                const existingWarning = target.parentElement.querySelector('.gift-quantity-warning');
                if (existingWarning) {
                  existingWarning.remove();
                }
                
                // Add warning temporarily
                target.parentElement.appendChild(message);
                setTimeout(() => {
                  if (message.parentNode) {
                    message.remove();
                  }
                }, 2000);
              }
            }
          }
        } catch (e) {
          // Silent fail
        }
      }
    });
    
    // Also monitor quantity increase buttons
    document.addEventListener('click', async function(e) {
      if (!giftSettings || !giftSettings.enabled) return;
      
      const target = e.target;
      
      // Check if this is a quantity increase button
      if (target.classList.contains('scd-item__btn') && target.getAttribute('data-qty-change') === 'inc') {
        const itemKey = target.getAttribute('data-id');
        if (!itemKey) return;
        
        // Get cart to check if this is the gift
        try {
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            const giftItem = cart.items.find(item => item.variant_id.toString() === giftSettings.variantId);
            
            if (giftItem && giftItem.key === itemKey && giftItem.quantity >= 1) {
              // Prevent increase if already at 1
              e.preventDefault();
              e.stopPropagation();
              
              // Show warning
              const message = document.createElement('div');
              message.textContent = 'Free gift quantity cannot exceed 1';
              message.style.cssText = 'color: #ef4444; font-size: 12px; margin-top: 4px;';
              message.className = 'gift-quantity-warning';
              
              // Remove existing warning
              const existingWarning = target.closest('.scd-item__quantity')?.querySelector('.gift-quantity-warning');
              if (existingWarning) {
                existingWarning.remove();
              }
              
              // Add warning
              const quantityContainer = target.closest('.scd-item__quantity');
              if (quantityContainer) {
                quantityContainer.appendChild(message);
                setTimeout(() => {
                  if (message.parentNode) {
                    message.remove();
                  }
                }, 2000);
              }
            }
          }
        } catch (e) {
          // Silent fail
        }
      }
    }, true);
  }

  /**
   * Initialize the free gift functionality
   */
  function init() {
    // Wait for MinimogSettings to be available
    function waitForSettings(callback, maxAttempts = 20) {
      let attempts = 0;
      const checkSettings = setInterval(() => {
        attempts++;
        if (window.MinimogSettings || attempts >= maxAttempts) {
          clearInterval(checkSettings);
          callback();
        }
      }, 100);
    }

    waitForSettings(() => {
      // Get settings
      giftSettings = getSettings();
      
      console.log('Free Gift Auto-Add: Initialized', {
        enabled: giftSettings.enabled,
        variantId: giftSettings.variantId
      });
      
      // Only proceed if feature is enabled
      if (!giftSettings.enabled) {
        console.log('Free Gift Auto-Add: Feature is disabled');
        return;
      }

      // Validate variant ID
      if (!giftSettings.variantId || giftSettings.variantId.trim() === '') {
        console.warn('Free Gift Auto-Add: Gift variant ID is not configured');
        return;
      }
      
      // Setup quantity monitoring
      setupQuantityMonitoring();
      
      // Monitor cart quantity changes to enforce max 1 (every 2 seconds)
      setInterval(async () => {
        if (giftSettings && giftSettings.enabled) {
          await enforceGiftQuantity();
        }
      }, 2000);
      
      // Pre-add gift on page load
      setTimeout(async () => {
        await addGiftToCart();
      }, 1000);
      
      // Listen for product additions to show notification
      setupProductAddListeners();
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
